import React from 'react';
import { DefaultButton } from '@fluentui/react';
import { TableInfoBar } from './TableInfoBar';
import styles from './StandardTable.module.css';

interface EmptyStateConfig {
  isFiltered: boolean;
  emptyMessage: string;
  filteredMessage: string;
}

interface StandardTableProps<T> {
  items: T[];
  totalCount?: number;
  loading?: boolean;
  filtersSummary?: string;
  sortSummary?: string;
  onClearFilters?: () => void;
  onRefresh?: () => void;
  onExportCsv?: () => void;
  renderTable: (pagedItems: T[]) => React.ReactNode;
  emptyState: EmptyStateConfig;
  pageSizeOptions?: number[];
  initialPageSize?: number;
  showPagination?: boolean;
  containerClassName?: string;
  keepPreviousTotals?: boolean;
}

export const StandardTable = <T,>({
  items,
  totalCount,
  loading = false,
  filtersSummary,
  sortSummary,
  onClearFilters,
  onRefresh,
  onExportCsv,
  renderTable,
  emptyState,
  pageSizeOptions = [10, 25, 50],
  initialPageSize = 10,
  showPagination = true,
  containerClassName,
  keepPreviousTotals = true,
}: StandardTableProps<T>) => {
  const [pageSize, setPageSize] = React.useState(initialPageSize);
  const [page, setPage] = React.useState(1);

  React.useEffect(() => {
    setPage(1);
  }, [items, pageSize]);

  const filteredCount = items.length;
  const totalItemsCount = totalCount ?? filteredCount;

  const totalsRef = React.useRef({ totalCount: totalItemsCount, filteredCount });
  React.useEffect(() => {
    if (!loading) {
      totalsRef.current = { totalCount: totalItemsCount, filteredCount };
    }
  }, [loading, totalItemsCount, filteredCount]);

  const displayTotals = loading && keepPreviousTotals ? totalsRef.current : { totalCount: totalItemsCount, filteredCount };

  const paginationEnabled = showPagination && filteredCount > 0;
  const pageCount = paginationEnabled ? Math.max(1, Math.ceil(filteredCount / pageSize)) : 1;
  const currentPage = Math.min(page, pageCount);
  const startIndex = paginationEnabled ? (currentPage - 1) * pageSize : 0;
  const pagedItems = paginationEnabled ? items.slice(startIndex, startIndex + pageSize) : items;
  const showingRange = displayTotals.filteredCount === 0
    ? { start: 0, end: 0 }
    : paginationEnabled
      ? { start: startIndex + 1, end: startIndex + pagedItems.length }
      : { start: 1, end: pagedItems.length };

  const emptyMessage = emptyState.isFiltered ? emptyState.filteredMessage : emptyState.emptyMessage;

  return (
    <div className={`${styles.tableCard} ${containerClassName || ''}`.trim()}>
      <TableInfoBar
        loading={loading}
        showingRange={showingRange}
        totalCount={displayTotals.totalCount}
        filteredCount={displayTotals.filteredCount}
        filtersSummary={filtersSummary}
        sortSummary={sortSummary}
        onClearFilters={onClearFilters}
        onRefresh={onRefresh}
        onExportCsv={onExportCsv}
      />

      {loading && items.length === 0 ? (
        <div className={styles.skeletonBody}>
          <div className={styles.skeletonRow} />
          <div className={styles.skeletonRow} />
          <div className={styles.skeletonRow} />
        </div>
      ) : items.length === 0 ? (
        <div className={styles.emptyState}>{emptyMessage}</div>
      ) : (
        <div className={styles.tableScroll}>{renderTable(pagedItems)}</div>
      )}

      {paginationEnabled && (
        <div className={styles.pagination}>
          <div className={styles.pageSummary}>
            Page {currentPage} of {pageCount}
          </div>
          <div className={styles.pageControls}>
            <DefaultButton
              text="Previous"
              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
            />
            <DefaultButton
              text="Next"
              onClick={() => setPage((prev) => Math.min(pageCount, prev + 1))}
              disabled={currentPage === pageCount}
            />
            <label className={styles.pageSizeLabel}>
              Rows
              <select
                value={pageSize}
                onChange={(event) => setPageSize(Number(event.target.value))}
              >
                {pageSizeOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>
      )}
    </div>
  );
};
